/**
 * CashManagementPage.js - Gestão de Caixa
 * Fase C - Cash Management
 * 
 * Features:
 * - Cash Sessions (Open/Close with balance reconciliation)
 * - Cash Transfers (TRF operations)
 * - Checkpoints (balance verification)
 * - Alerts (missing operations)
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cashAPI, catalogsAPI, settingsAPI, getErrorMessage, attachmentsAPI } from '@/lib/api';
import GlobalFilters from '@/components/GlobalFilters';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import AttachmentUploader from '@/components/AttachmentUploader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Play, Square, ArrowRightLeft, Clock, AlertTriangle, Check, Eye,
  DollarSign, Wallet, Building2, MapPin, History, Bell, BellRing,
  Banknote, Send, ArrowRight, ChevronDown, RefreshCw, Calculator, Search
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatDate, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import ExportButton from '@/components/ExportButton';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { COMPACT_ACTION_BUTTONS, TABLE_DENSITY } from '@/lib/tableDensity';
import { useLanguage } from '@/i18n';
import { applyCleanOrLegacyParam, buildHandleMaps, resolveHandleOrLegacyId } from '@/lib/queryHandles';

// Session and direction styles
const SESSION_STATUS_STYLES = {
  open: { color: 'bg-green-100 text-green-700 border-green-200', icon: Play },
  closed: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Square },
  pending_reconciliation: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
};
const CASH_TABLE_CLASS = TABLE_DENSITY.table;
const CASH_TABLE_HEAD_LEFT = `${TABLE_DENSITY.head} text-left font-medium normal-case tracking-normal`;
const CASH_TABLE_HEAD_RIGHT = `${TABLE_DENSITY.head} text-right font-medium normal-case tracking-normal`;
const CASH_TABLE_HEAD_CENTER = `${TABLE_DENSITY.head} text-center font-medium normal-case tracking-normal`;

const DIRECTION_STYLES = {
  in: { color: 'text-green-600', bgColor: 'bg-green-50 border-green-200' },
  out: { color: 'text-red-600', bgColor: 'bg-red-50 border-red-200' },
  transfer: { color: 'text-blue-600', bgColor: 'bg-blue-50 border-blue-200' },
};

const ALERT_STATUS_STYLES = {
  open: 'bg-amber-100 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  invalid_scope: 'bg-slate-100 text-slate-700 border-slate-200',
  stale_history: 'bg-blue-100 text-blue-700 border-blue-200',
};

const matchesOperationalSearch = (query, fields) => {
  if (!query) return true;
  return fields.some((field) => String(field || '').toLowerCase().includes(query));
};

export default function CashManagementPage() {
  const navigate = useNavigate();
  // Data state
  const [sessions, setSessions] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [operationTypes, setOperationTypes] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [classificationSummary, setClassificationSummary] = useState({ counts: { active_valid: 0, resolved: 0, invalid_location_scope: 0, stale_past_day: 0 }, items: [] });
  const [openAlertsSnapshot, setOpenAlertsSnapshot] = useState([]);
  const [loading, setLoading] = useState(true);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  
  // URL Search Params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedLocationHandle = searchParams.get('location');
  
  // Global filters
  const { filters, setFilters } = useGlobalFilters({
    period: searchParams.get('period') || 'today',
    location_id: searchParams.get('location_id') || null,
  });
  
  // Active session state
  const [activeSession, setActiveSession] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const locationHandleMaps = useMemo(() => buildHandleMaps(locations, (location) => location.name), [locations]);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('sessions');
  
  // Dialog state
  const [openSessionDialogOpen, setOpenSessionDialogOpen] = useState(false);
  const [closeSessionDialogOpen, setCloseSessionDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [checkpointDialogOpen, setCheckpointDialogOpen] = useState(false);
  const [sessionDetailsOpen, setSessionDetailsOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // State for transfer details dialog
  const [transferDetailsOpen, setTransferDetailsOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [selectedTransferAttachments, setSelectedTransferAttachments] = useState([]);
  
  const { hasRole, user } = useAuth();
  const { t } = useLanguage();
  const canManage = hasRole(['admin', 'store_manager', 'accountant']);
  // Form state
  const [openSessionData, setOpenSessionData] = useState({ location_id: '', opening_balance: '', notes: '', opened_at: '' });
  const [closeSessionData, setCloseSessionData] = useState({ closing_balance: '', difference_justification: '', notes: '' });
  const [transferData, setTransferData] = useState({
    operation_type_id: '',
    source_account_id: '',
    destination_account_id: '',
    amount: '',
    location_id: '',
    beneficiary_name: '',
    reason: '',
    reference: '',
    notes: '',
    registered_at: ''  // Custom date/time for late registrations
  });
  const [checkpointData, setCheckpointData] = useState({ location_id: '', balance: '', notes: '', checkpoint_time: '' });
  const autoOpenSessionHandledRef = useRef(false);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.period && filters.period !== 'today') params.set('period', filters.period);
    applyCleanOrLegacyParam({
      params,
      cleanKey: 'location',
      legacyKey: 'location_id',
      value: filters.location_id,
      idToHandle: locationHandleMaps.idToHandle,
    });
    if (searchParams.get('open_session') === '1') params.set('open_session', '1');
    setSearchParams(params, { replace: true });
  }, [filters, searchParams, setSearchParams, locationHandleMaps.idToHandle]);
  
  useEffect(() => {
    const shouldAutoOpen = searchParams.get('open_session') === '1';
    if (!shouldAutoOpen || autoOpenSessionHandledRef.current) return;
    if (!canManage) return;
    if (!selectedLocation) return;
    if (activeSession) return;

    autoOpenSessionHandledRef.current = true;
    setActiveTab('sessions');
    setOpenSessionData((prev) => ({ ...prev, location_id: selectedLocation }));
    setOpenSessionDialogOpen(true);

    const params = new URLSearchParams(searchParams);
    params.delete('open_session');
    setSearchParams(params, { replace: true });
  }, [searchParams, selectedLocation, activeSession, canManage, setSearchParams]);

  const loadActiveSession = useCallback(async (locationId) => {
    try {
      const response = await cashAPI.getActiveSession(locationId);
      setActiveSession(response.data || null);
    } catch {
      setActiveSession(null);
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      resetFetchTelemetry();

      const sessionParams = { period: filters.period || 'today' };
      if (filters.location_id) sessionParams.location_id = filters.location_id;

      const alertParams = { status: 'open' };
      if (filters.location_id) alertParams.location_id = filters.location_id;

      const [operationTypesRes, accountsRes, locationsRes, sessionsRes, transfersRes, alertsRes, checkpointsRes, alertClassificationRes] = await Promise.all([
        safeRequest(() => catalogsAPI.listCashOperationTypes(), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'catalogs.cash_operation_types', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActivePaymentAccounts(), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'settings.payment_accounts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActiveLocations(), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => cashAPI.listSessions(sessionParams), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'cash.sessions', onEvent: captureFetchTelemetry } }),
        safeRequest(() => cashAPI.listTransfers(sessionParams), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'cash.transfers', onEvent: captureFetchTelemetry } }),
        safeRequest(() => cashAPI.listAlerts(alertParams), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'cash.alerts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => cashAPI.listCheckpoints(filters.location_id ? { location_id: filters.location_id, limit: 50 } : { limit: 50 }), { data: [] }, { telemetry: { module: 'cash-management', endpoint: 'cash.checkpoints', onEvent: captureFetchTelemetry } }),
        safeRequest(() => cashAPI.getAlertClassificationSummary(), { data: { counts: { active_valid: 0, resolved: 0, invalid_location_scope: 0, stale_past_day: 0 }, items: [] } }, { telemetry: { module: 'cash-management', endpoint: 'cash.alerts.classification_summary', onEvent: captureFetchTelemetry } })
      ]);
      
      setOperationTypes(operationTypesRes.data || []);
      setPaymentAccounts(accountsRes.data || []);
      setLocations(locationsRes.data || []);
      setSessions(sessionsRes.data || []);
      setTransfers(transfersRes.data || []);
      setAlerts(alertsRes.data || []);
      setCheckpoints(checkpointsRes.data || []);
      setOpenAlertsSnapshot(alertsRes.data || []);
      setClassificationSummary(alertClassificationRes.data || { counts: { active_valid: 0, resolved: 0, invalid_location_scope: 0, stale_past_day: 0 }, items: [] });

      const availableLocations = locationsRes.data || [];
      const requestedLocationId = resolveHandleOrLegacyId({
        cleanValue: requestedLocationHandle,
        legacyValue: searchParams.get('location_id') || filters.location_id || null,
        handleToId: buildHandleMaps(availableLocations, (location) => location.name).handleToId,
      });
      const requestedLocation = requestedLocationId
        ? availableLocations.find((location) => location.id === requestedLocationId)
        : null;
      let nextSelectedLocation = '';

      if (requestedLocation) {
        nextSelectedLocation = requestedLocation.id;
        setSelectedLocation(requestedLocation.id);
        if (filters.location_id !== requestedLocation.id) {
          setFilters({ location_id: requestedLocation.id });
        }
      } else if (availableLocations.length === 1) {
        nextSelectedLocation = availableLocations[0].id;
        setSelectedLocation(availableLocations[0].id);
        if (filters.location_id !== availableLocations[0].id) {
          setFilters({ location_id: availableLocations[0].id });
        }
      } else {
        setSelectedLocation('');
      }

      if (nextSelectedLocation) {
        await loadActiveSession(nextSelectedLocation);
      } else {
        setActiveSession(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar dados de caixa'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [captureFetchTelemetry, filters.location_id, filters.period, loadActiveSession, requestedLocationHandle, resetFetchTelemetry, searchParams]);

  const loadSessions = useCallback(async () => {
    try {
      const params = { period: filters.period || 'today' };
      if (filters.location_id) params.location_id = filters.location_id;
      const response = await safeRequest(() => cashAPI.listSessions(params), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'cash.sessions', onEvent: captureFetchTelemetry } });
      setSessions(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar sessões de caixa'));
      console.error(error);
    }
  }, [captureFetchTelemetry, filters]);

  const loadTransfers = useCallback(async () => {
    try {
      const params = { period: filters.period || 'today' };
      if (filters.location_id) params.location_id = filters.location_id;
      const response = await safeRequest(() => cashAPI.listTransfers(params), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'cash.transfers', onEvent: captureFetchTelemetry } });
      setTransfers(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar transferências'));
      console.error(error);
    }
  }, [captureFetchTelemetry, filters]);

  const loadCheckpoints = useCallback(async () => {
    try {
      const params = filters.location_id ? { location_id: filters.location_id, limit: 50 } : { limit: 50 };
      const response = await safeRequest(() => cashAPI.listCheckpoints(params), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'cash.checkpoints', onEvent: captureFetchTelemetry } });
      setCheckpoints(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar checkpoints'));
      console.error(error);
    }
  }, [captureFetchTelemetry, filters.location_id]);

  const loadAlerts = useCallback(async () => {
    try {
      const params = { status: 'open' };
      if (filters.location_id) params.location_id = filters.location_id;
      const response = await safeRequest(() => cashAPI.listAlerts(params), { data: [] }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'cash.alerts', onEvent: captureFetchTelemetry } });
      const nextAlerts = response.data || [];
      setAlerts(nextAlerts);
      setOpenAlertsSnapshot(nextAlerts);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar alertas de caixa'));
      console.error(error);
    }
  }, [captureFetchTelemetry, filters.location_id]);

  const loadAlertClassificationSummary = useCallback(async () => {
    try {
      const response = await safeRequest(() => cashAPI.getAlertClassificationSummary(), { data: { counts: { active_valid: 0, resolved: 0, invalid_location_scope: 0, stale_past_day: 0 }, items: [] } }, { retries: 1, required: true, telemetry: { module: 'cash-management', endpoint: 'cash.alerts.classification_summary', onEvent: captureFetchTelemetry } });
      setClassificationSummary(response.data || { counts: { active_valid: 0, resolved: 0, invalid_location_scope: 0, stale_past_day: 0 }, items: [] });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar classificação dos alertas'));
      console.error(error);
    }
  }, [captureFetchTelemetry]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, [setFilters]);

  // Refresh function for GlobalFilters
  const handleRefresh = useCallback(() => {
    loadSessions();
    loadTransfers();
    loadAlerts();
    loadCheckpoints();
    loadAlertClassificationSummary();
  }, [loadAlertClassificationSummary, loadAlerts, loadCheckpoints, loadSessions, loadTransfers]);

  const navigateToPos = useCallback((tab = 'cash') => {
    const params = new URLSearchParams();
    params.set('tab', tab);
    if (selectedLocation) {
      params.set('location_id', selectedLocation);
    }
    navigate(`/sales?${params.toString()}`);
  }, [navigate, selectedLocation]);

  // ==========================================================================
  // ACTIONS
  // ==========================================================================
  
  const handleOpenSession = async () => {
    if (!openSessionData.location_id || !openSessionData.opening_balance) {
      toast.error('Preencha a localização e o saldo inicial');
      return;
    }
    
    try {
      await cashAPI.openSession({
        location_id: openSessionData.location_id,
        opening_balance: parseFloat(openSessionData.opening_balance),
        notes: openSessionData.notes,
        opened_at: openSessionData.opened_at ? new Date(openSessionData.opened_at).toISOString() : null
      });
      
      toast.success('Sessão de caixa aberta!');
      setOpenSessionDialogOpen(false);
      setOpenSessionData({ location_id: '', opening_balance: '', notes: '', opened_at: '' });
      loadSessions();
      loadActiveSession(openSessionData.location_id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao abrir sessão'));
    }
  };
  
  const handleCloseSession = async () => {
    if (!activeSession) return;
    
    if (!closeSessionData.closing_balance) {
      toast.error('Informe o saldo de fecho');
      return;
    }
    
    // Calculate expected balance
    const expected = (
      activeSession.opening_balance +
      (activeSession.total_sales || 0) +
      (activeSession.total_transfers_in || 0) -
      (activeSession.total_expenses || 0) -
      (activeSession.total_transfers_out || 0)
    );
    
    const difference = parseFloat(closeSessionData.closing_balance) - expected;
    
    if (Math.abs(difference) > 1 && !closeSessionData.difference_justification) {
      toast.error(`Diferença de ${formatCurrency(difference)}. Justificação obrigatória.`);
      return;
    }
    
    try {
      await cashAPI.closeSession(activeSession.id, {
        closing_balance: parseFloat(closeSessionData.closing_balance),
        difference_justification: closeSessionData.difference_justification,
        notes: closeSessionData.notes
      });
      
      toast.success('Sessão de caixa fechada!');
      setCloseSessionDialogOpen(false);
      setCloseSessionData({ closing_balance: '', difference_justification: '', notes: '' });
      setActiveSession(null);
      loadSessions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao fechar sessão'));
    }
  };
  
  const handleCreateTransfer = async () => {
    if (!transferData.operation_type_id || !transferData.source_account_id || 
        !transferData.amount || !transferData.location_id || !transferData.reason) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    const selectedOpType = operationTypes.find(t => t.id === transferData.operation_type_id);
    if (selectedOpType?.direction === 'transfer' && !transferData.destination_account_id) {
      toast.error('Conta de destino obrigatória para transferências');
      return;
    }
    
    try {
      const payload = {
        ...transferData,
        amount: parseFloat(transferData.amount),
        registered_at: transferData.registered_at ? new Date(transferData.registered_at).toISOString() : null
      };
      
      await cashAPI.createTransfer(payload);
      
      toast.success('Transferência registada!');
      setTransferDialogOpen(false);
      setTransferData({
        operation_type_id: '',
        source_account_id: '',
        destination_account_id: '',
        amount: '',
        location_id: selectedLocation || '',
        beneficiary_name: '',
        reason: '',
        reference: '',
        notes: '',
        registered_at: ''
      });
      loadTransfers();
      if (selectedLocation) {
        loadActiveSession(selectedLocation);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar transferência'));
    }
  };
  
  const handleRegisterCheckpoint = async () => {
    if (!checkpointData.location_id || !checkpointData.balance) {
      toast.error('Preencha a localização e o saldo');
      return;
    }
    
    try {
      await cashAPI.registerCheckpoint(
        checkpointData.location_id,
        parseFloat(checkpointData.balance),
        checkpointData.notes,
        checkpointData.checkpoint_time ? new Date(checkpointData.checkpoint_time).toISOString() : null
      );
      
      toast.success('Checkpoint registado!');
      setCheckpointDialogOpen(false);
      setCheckpointData({ location_id: '', balance: '', notes: '', checkpoint_time: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao registar checkpoint'));
    }
  };
  
  // State for alert resolution dialog
  const [resolveAlertDialogOpen, setResolveAlertDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [alertStatusFilter, setAlertStatusFilter] = useState('open');
  const [sessionStatusFilter, setSessionStatusFilter] = useState('all');
  const [transferDirectionFilter, setTransferDirectionFilter] = useState('all');
  const [transferOperationFilter, setTransferOperationFilter] = useState('all');
  const [alertTypeFilter, setAlertTypeFilter] = useState('all');
  const [cashSearchTerm, setCashSearchTerm] = useState('');
  
  const handleOpenResolveDialog = (alert) => {
    setSelectedAlert(alert);
    setResolutionNotes('');
    setResolveAlertDialogOpen(true);
  };
  
  const handleResolveAlert = async () => {
    if (!selectedAlert) return;
    
    if (!resolutionNotes.trim()) {
      toast.error('Comentário obrigatório para resolver o alerta');
      return;
    }
    
    try {
      await cashAPI.resolveAlert(selectedAlert.id, resolutionNotes.trim());
      toast.success('Alerta resolvido');
      setResolveAlertDialogOpen(false);
      setSelectedAlert(null);
      setResolutionNotes('');
      loadAlerts();
      loadAlertClassificationSummary();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao resolver alerta'));
    }
  };

  const handleOpenSessionFromAlert = useCallback((alert) => {
    if (!alert?.location_id) return;
    setSelectedLocation(alert.location_id);
    if (filters.location_id !== alert.location_id) {
      setFilters({ location_id: alert.location_id });
    }
    setOpenSessionData((prev) => ({ ...prev, location_id: alert.location_id }));
    setActiveTab('sessions');
    setOpenSessionDialogOpen(true);
  }, [filters.location_id, setFilters]);
  
  const handleViewSession = (session) => {
    setSelectedSession(session);
    setSessionDetailsOpen(true);
  };
  
  const handleViewTransfer = async (transfer) => {
    setSelectedTransfer(transfer);
    setTransferDetailsOpen(true);
    // Load attachments for this transfer
    try {
      const response = await attachmentsAPI.listByEntity('cash_transfer', transfer.transfer_code);
      setSelectedTransferAttachments(response.data || []);
    } catch (error) {
      setSelectedTransferAttachments([]);
    }
  };

  const loadAlertsWithFilter = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (alertStatusFilter === 'open') {
        params.status = 'open';
      } else if (alertStatusFilter === 'resolved') {
        params.status = 'resolved';
      } else if (alertStatusFilter === 'invalid_scope') {
        params.status = 'invalid_scope';
      } else if (alertStatusFilter === 'stale_history') {
        params.status = 'stale_history';
      }
      if (filters.location_id) {
        params.location_id = filters.location_id;
      }
      const response = await safeRequest(() => cashAPI.listAlerts(params), { data: [] }, { telemetry: { module: 'cash-management', endpoint: 'cash.alerts.filtered', onEvent: captureFetchTelemetry } });
      setAlerts(response.data || []);
    } catch (error) {
      console.error(error);
    }
  }, [alertStatusFilter, captureFetchTelemetry, filters.location_id]);

  // Load alerts based on filter
  useEffect(() => {
    if (!loading) {
      loadAlertsWithFilter();
    }
  }, [alertStatusFilter, loadAlertsWithFilter, loading]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!loading) {
      loadSessions();
      loadTransfers();
      loadAlerts();
      loadCheckpoints();
      if (filters.location_id) {
        loadActiveSession(filters.location_id);
      }
    }
  }, [filters, loading, loadActiveSession, loadAlerts, loadCheckpoints, loadSessions, loadTransfers]);

  // Count of unresolved alerts
  const unresolvedAlertsCount = useMemo(() => {
    return openAlertsSnapshot.length;
  }, [openAlertsSnapshot]);

  const sessionStatusConfig = useMemo(() => ({
    open: { ...SESSION_STATUS_STYLES.open, label: t('cashManagementPage.statusLabels.open') },
    closed: { ...SESSION_STATUS_STYLES.closed, label: t('cashManagementPage.statusLabels.closed') },
    pending_reconciliation: { ...SESSION_STATUS_STYLES.pending_reconciliation, label: t('cashManagementPage.statusLabels.pending_reconciliation') },
  }), [t]);

  const directionConfig = useMemo(() => ({
    in: { ...DIRECTION_STYLES.in, label: t('cashManagementPage.directionLabels.in') },
    out: { ...DIRECTION_STYLES.out, label: t('cashManagementPage.directionLabels.out') },
    transfer: { ...DIRECTION_STYLES.transfer, label: t('cashManagementPage.directionLabels.transfer') },
  }), [t]);

  const alertStatusConfig = useMemo(() => ({
    open: { label: t('cashManagementPage.alertStatusLabels.open'), className: ALERT_STATUS_STYLES.open },
    resolved: { label: t('cashManagementPage.alertStatusLabels.resolved'), className: ALERT_STATUS_STYLES.resolved },
    invalid_scope: { label: t('cashManagementPage.alertStatusLabels.invalid_scope'), className: ALERT_STATUS_STYLES.invalid_scope },
    stale_history: { label: t('cashManagementPage.alertStatusLabels.stale_history'), className: ALERT_STATUS_STYLES.stale_history },
  }), [t]);

  const alertTypeConfig = useMemo(() => ({
    missed_checkpoint: { label: t('cashManagementPage.alertTypes.missedCheckpoint'), className: 'bg-orange-100 text-orange-700 border-orange-200' },
    no_opening: { label: t('cashManagementPage.alertTypes.noOpening'), className: 'bg-rose-100 text-rose-700 border-rose-200' },
    no_closing: { label: t('cashManagementPage.alertTypes.noClosing'), className: 'bg-purple-100 text-purple-700 border-purple-200' },
  }), [t]);

  const normalizedCashSearch = useMemo(() => cashSearchTerm.trim().toLowerCase(), [cashSearchTerm]);

  const filteredSessions = useMemo(() => {
    const baseSessions = sessionStatusFilter === 'all' ? sessions : sessions.filter((session) => session.status === sessionStatusFilter);
    return baseSessions.filter((session) => matchesOperationalSearch(normalizedCashSearch, [session.session_code, session.location_name, session.opened_by_name, session.closed_by_name]));
  }, [normalizedCashSearch, sessionStatusFilter, sessions]);

  const filteredTransfers = useMemo(() => {
    return transfers.filter((transfer) => {
      if (transferDirectionFilter !== 'all' && transfer.operation_direction !== transferDirectionFilter) return false;
      if (transferOperationFilter !== 'all' && transfer.operation_type_id !== transferOperationFilter) return false;
      if (!matchesOperationalSearch(normalizedCashSearch, [transfer.transfer_code, transfer.reason, transfer.source_account_name, transfer.destination_account_name, transfer.operation_type_name, transfer.beneficiary_name])) return false;
      return true;
    });
  }, [normalizedCashSearch, transferDirectionFilter, transferOperationFilter, transfers]);

  const filteredAlerts = useMemo(() => {
    const baseAlerts = alertTypeFilter === 'all' ? alerts : alerts.filter((alert) => alert.alert_type === alertTypeFilter);
    return baseAlerts.filter((alert) => matchesOperationalSearch(normalizedCashSearch, [alert.alert_code, alert.location_name, alert.message, alert.resolution_notes, alert.archival_reason_code]));
  }, [alertTypeFilter, alerts, normalizedCashSearch]);

  const sessionCounts = useMemo(() => ({
    open: sessions.filter((session) => session.status === 'open').length,
    closed: sessions.filter((session) => session.status === 'closed').length,
    pending_reconciliation: sessions.filter((session) => session.status === 'pending_reconciliation').length,
  }), [sessions]);

  const transferCounts = useMemo(() => ({
    in: transfers.filter((transfer) => transfer.operation_direction === 'in').length,
    out: transfers.filter((transfer) => transfer.operation_direction === 'out').length,
    transfer: transfers.filter((transfer) => transfer.operation_direction === 'transfer').length,
  }), [transfers]);

  const alertCounts = useMemo(() => ({
    missed_checkpoint: openAlertsSnapshot.filter((alert) => alert.alert_type === 'missed_checkpoint').length,
    no_opening: openAlertsSnapshot.filter((alert) => alert.alert_type === 'no_opening').length,
    no_closing: openAlertsSnapshot.filter((alert) => alert.alert_type === 'no_closing').length,
  }), [openAlertsSnapshot]);

  const archivedAlertCount = useMemo(() => (classificationSummary?.counts?.invalid_location_scope || 0) + (classificationSummary?.counts?.stale_past_day || 0), [classificationSummary]);

  const selectedLocationOpenAlerts = useMemo(() => {
    if (!selectedLocation) return [];
    return openAlertsSnapshot.filter((alert) => alert.location_id === selectedLocation);
  }, [openAlertsSnapshot, selectedLocation]);

  const selectedLocationName = useMemo(() => locations.find((location) => location.id === selectedLocation)?.name || '', [locations, selectedLocation]);

  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================
  
  // Selected operation type details
  const selectedOperationType = useMemo(() => {
    return operationTypes.find(t => t.id === transferData.operation_type_id);
  }, [transferData.operation_type_id, operationTypes]);
  
  // Expected balance calculation for close session
  const expectedBalance = useMemo(() => {
    if (!activeSession) return 0;
    return (
      activeSession.opening_balance +
      (activeSession.total_sales || 0) +
      (activeSession.total_transfers_in || 0) -
      (activeSession.total_expenses || 0) -
      (activeSession.total_transfers_out || 0)
    );
  }, [activeSession]);
  const hasCashManagementData = Boolean(
    locations.length ||
    sessions.length ||
    transfers.length ||
    alerts.length ||
    operationTypes.length ||
    paymentAccounts.length ||
    activeSession
  );

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className="p-6 space-y-4" data-testid="cash-management-page">
      {/* Header */}
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{fontFamily: 'Manrope'}}>
            {t('cashManagementPage.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('cashManagementPage.description')}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => navigateToPos('cash')}
            data-testid="cash-management-open-pos-cash-tab-button"
          >
            <Wallet className="mr-2 h-4 w-4" />
            {t('cashManagementPage.posCash')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigateToPos('sales')}
            data-testid="cash-management-open-pos-sales-tab-button"
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            {t('cashManagementPage.posSales')}
          </Button>
          {/* Location Selector for active operations */}
          <Select
            value={selectedLocation}
            onValueChange={(value) => {
              setSelectedLocation(value);
              if (filters.location_id !== value) {
                setFilters({ location_id: value });
              }
              loadActiveSession(value);
            }}
          >
            <SelectTrigger className="w-48" data-testid="location-selector">
              <MapPin className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t('cashManagementPage.selectLocation')} />
            </SelectTrigger>
            <SelectContent>
              {locations.map(loc => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Global Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="flex-grow">
          <GlobalFilters
            filters={['period', 'location']}
            onChange={handleFilterChange}
            onRefresh={handleRefresh}
            loading={loading}
            defaultPeriod="today"
          />
        </div>
        <ExportButton 
          module="cash" 
          filters={{
            period: filters.period,
            location_id: filters.location_id,
            status: filters.status
          }}
        />
      </div>

      <ModuleStateBoundary
        loading={loading}
        hasData={hasCashManagementData}
        partialIssues={partialIssues}
        loadingTestId="cash-management-loading-state"
        partialIssuesTestId="cash-management-partial-issues"
        emptyState={(
          <EmptyStatePanel
            title={t('cashManagementPage.emptyTitle')}
            description={t('cashManagementPage.emptyDescription')}
            ctaLabel={t('cashManagementPage.emptyCta')}
            ctaTo="/settings"
            testId="cash-management-empty-state"
          />
        )}
      >

      <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="cash-management-daily-cockpit-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wallet className="h-5 w-5 text-emerald-600" />
            {t('cashManagementPage.dailyCockpitTitle')}
          </CardTitle>
          <CardDescription>{t('cashManagementPage.dailyCockpitDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="cash-cockpit-session-state-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('cashManagementPage.operationalCards.sessionState')}</span>
                <Play className="h-4 w-4 text-slate-400" />
              </div>
              <p className="text-lg font-semibold text-slate-900">
                {!selectedLocation ? t('cashManagementPage.operationalCards.noLocation') : activeSession ? t('cashManagementPage.operationalCards.sessionOpen') : t('cashManagementPage.operationalCards.pendingOpening')}
              </p>
              <p className="mt-1 text-sm text-slate-500">{selectedLocationName || t('cashManagementPage.operationalCards.noLocation')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                disabled={!selectedLocation || !!activeSession}
                onClick={() => {
                  setOpenSessionData({ ...openSessionData, location_id: selectedLocation });
                  setOpenSessionDialogOpen(true);
                }}
                data-testid="cash-cockpit-open-session-button"
              >
                {activeSession ? t('cashManagementPage.operationalCards.noImmediateAction') : t('cashManagementPage.operationalCards.openSessionAction')}
              </Button>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="cash-cockpit-active-alerts-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium uppercase tracking-wide text-amber-700">{t('cashManagementPage.operationalCards.activeAlerts')}</span>
                <BellRing className="h-4 w-4 text-amber-500" />
              </div>
              <p className="text-2xl font-semibold text-amber-800">{unresolvedAlertsCount}</p>
              <p className="mt-1 text-sm text-amber-700">{t('cashManagementPage.operationalCards.activeAlertsHelper', { count: unresolvedAlertsCount })}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 bg-white"
                onClick={() => {
                  setActiveTab('alerts');
                  setAlertStatusFilter('open');
                }}
                data-testid="cash-cockpit-open-alerts-button"
              >
                {t('cashManagementPage.operationalCards.openAlertsAction')}
              </Button>
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4" data-testid="cash-cockpit-archived-alerts-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium uppercase tracking-wide text-blue-700">{t('cashManagementPage.operationalCards.archivedAlerts')}</span>
                <History className="h-4 w-4 text-blue-500" />
              </div>
              <p className="text-2xl font-semibold text-blue-800">{archivedAlertCount}</p>
              <div className="mt-1 flex flex-wrap gap-2 text-xs text-blue-700">
                <span>{classificationSummary?.counts?.invalid_location_scope || 0} invalid_scope</span>
                <span>•</span>
                <span>{classificationSummary?.counts?.stale_past_day || 0} stale_history</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 bg-white"
                onClick={() => {
                  setActiveTab('alerts');
                  setAlertStatusFilter(classificationSummary?.counts?.invalid_location_scope ? 'invalid_scope' : 'stale_history');
                }}
                data-testid="cash-cockpit-open-archived-button"
              >
                {t('cashManagementPage.operationalCards.openArchivedAction')}
              </Button>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="cash-cockpit-movements-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium uppercase tracking-wide text-emerald-700">{t('cashManagementPage.operationalCards.movements')}</span>
                <ArrowRightLeft className="h-4 w-4 text-emerald-500" />
              </div>
              <p className="text-lg font-semibold text-emerald-800">{t('cashManagementPage.operationalCards.sessionsHelper', { count: sessions.length })}</p>
              <p className="mt-1 text-sm text-emerald-700">{t('cashManagementPage.operationalCards.checkpointsHelper', { count: checkpoints.length })}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-emerald-700">
                <span data-testid="cash-cockpit-transfers-count">{transfers.length} transferências</span>
                <span>•</span>
                <span data-testid="cash-cockpit-open-sessions-count">{sessionCounts.open} abertas</span>
              </div>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="cash-management-search-card">
              <div className="flex items-center gap-3">
                <Search className="h-4 w-4 text-slate-400" />
                <Input
                  value={cashSearchTerm}
                  onChange={(event) => setCashSearchTerm(event.target.value)}
                  placeholder={t('cashManagementPage.searchPlaceholder')}
                  data-testid="cash-management-quick-search-input"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">{t('cashManagementPage.searchHint')}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setTransferData({ ...transferData, location_id: selectedLocation });
                  setTransferDialogOpen(true);
                }}
                disabled={!activeSession}
                data-testid="new-transfer-btn"
              >
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                {t('cashManagementPage.newTransfer')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setCheckpointData({ ...checkpointData, location_id: selectedLocation });
                  setCheckpointDialogOpen(true);
                }}
                data-testid="checkpoint-btn"
              >
                <Calculator className="h-4 w-4 mr-2" />
                {t('cashManagementPage.registerCheckpoint')}
              </Button>
              <Button variant="outline" onClick={() => { setActiveTab('alerts'); setAlertStatusFilter('open'); }} data-testid="cash-management-view-alerts-button">
                <Bell className="h-4 w-4 mr-2" />
                {t('cashManagementPage.viewAlerts')}
              </Button>
              <Button variant="outline" onClick={handleRefresh} data-testid="cash-management-refresh-button">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('cashManagementPage.refresh')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Session Card */}
      {selectedLocation && (
        <Card className={cn(
          "border-2",
          activeSession ? "border-green-300 bg-green-50/50" : "border-slate-200"
        )}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={cn(
                  "p-3 rounded-full",
                  activeSession ? "bg-green-100" : "bg-slate-100"
                )}>
                  {activeSession ? <Play className="h-6 w-6 text-green-600" /> : <Square className="h-6 w-6 text-slate-400" />}
                </div>
                <div>
                  <h3 className="font-semibold">
                    {activeSession ? `${t('cashManagementPage.sessionLabel')} ${activeSession.session_code}` : t('cashManagementPage.noActiveSession')}
                  </h3>
                  {activeSession ? (
                    <div className="text-sm text-muted-foreground">
                      {t('cashManagementPage.sessionOpenedBy', { date: formatDateTime(activeSession.opened_at), name: activeSession.opened_by_name })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('cashManagementPage.openSessionHint')}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {activeSession && (
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">{t('cashManagementPage.expectedBalance')}</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(expectedBalance)}</p>
                  </div>
                )}
                
                {!activeSession ? (
                  <Button onClick={() => {
                    setOpenSessionData({ ...openSessionData, location_id: selectedLocation });
                    setOpenSessionDialogOpen(true);
                  }} data-testid="open-session-btn">
                    <Play className="h-4 w-4 mr-2" />
                    {t('cashManagementPage.openSessionAction')}
                  </Button>
                ) : (
                  <Button variant="destructive" onClick={() => setCloseSessionDialogOpen(true)} data-testid="close-session-btn">
                    <Square className="h-4 w-4 mr-2" />
                    {t('cashManagementPage.closeSessionAction')}
                  </Button>
                )}
              </div>
            </div>
            
            {/* Session Stats */}
            {activeSession && (
              <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
                <div>
                  <p className="text-xs text-muted-foreground">{t('cashManagementPage.openingBalance')}</p>
                  <p className="font-semibold">{formatCurrency(activeSession.opening_balance)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('cashManagementPage.entries')}</p>
                  <p className="font-semibold text-green-600">+{formatCurrency(activeSession.total_transfers_in || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('cashManagementPage.exits')}</p>
                  <p className="font-semibold text-red-600">-{formatCurrency(activeSession.total_transfers_out || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('cashManagementPage.expenses')}</p>
                  <p className="font-semibold text-amber-600">-{formatCurrency(activeSession.total_expenses || 0)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alerts Banner */}
      {unresolvedAlertsCount > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <BellRing className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  {t('cashManagementPage.pendingAlertsCount', { count: unresolvedAlertsCount })}
                </p>
                {selectedLocation && selectedLocationOpenAlerts.length > 0 && (
                  <p className="text-sm text-amber-700 mt-1" data-testid="cash-management-selected-location-alerts">
                    {t('cashManagementPage.selectedLocationFocus')} · {t('cashManagementPage.selectedLocationAlerts', { count: selectedLocationOpenAlerts.length })}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  {openAlertsSnapshot.slice(0, 3).map(alert => (
                    <Badge key={alert.id} variant="outline" className="bg-white">
                      {alert.message}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-5 ml-1 px-1"
                        onClick={() => handleOpenResolveDialog(alert)}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      {!activeSession && alert.alert_type === 'no_opening' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 ml-1 px-1"
                          onClick={() => handleOpenSessionFromAlert(alert)}
                          data-testid={`cash-banner-open-session-${alert.id}`}
                        >
                          <Play className="h-3 w-3" />
                        </Button>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="sessions" data-testid="tab-sessions">
            <Play className="h-4 w-4 mr-2" />
            {t('cashManagementPage.tabs.sessions')}
            <Badge variant="secondary" className="ml-2">{sessions.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            {t('cashManagementPage.tabs.transfers')}
            <Badge variant="secondary" className="ml-2">{transfers.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <Bell className="h-4 w-4 mr-2" />
            {t('cashManagementPage.tabs.alerts')}
            {unresolvedAlertsCount > 0 && (
              <Badge variant="destructive" className="ml-2">{unresolvedAlertsCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200" data-testid="cash-session-summary-open">{t('cashManagementPage.sessionSummary.open', { count: sessionCounts.open })}</Badge>
                  <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200" data-testid="cash-session-summary-closed">{t('cashManagementPage.sessionSummary.closed', { count: sessionCounts.closed })}</Badge>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="cash-session-summary-pending">{t('cashManagementPage.sessionSummary.pending', { count: sessionCounts.pending_reconciliation })}</Badge>
                </div>
                <div className="w-full lg:w-64">
                  <Select value={sessionStatusFilter} onValueChange={setSessionStatusFilter}>
                    <SelectTrigger data-testid="cash-session-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('cashManagementPage.filters.allSessionStatuses')}</SelectItem>
                      <SelectItem value="open">{t('cashManagementPage.statusLabels.open')}</SelectItem>
                      <SelectItem value="closed">{t('cashManagementPage.statusLabels.closed')}</SelectItem>
                      <SelectItem value="pending_reconciliation">{t('cashManagementPage.statusLabels.pending_reconciliation')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredSessions.length === 0 ? (
                <div className="text-center py-12">
                  <Play className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">{t('cashManagementPage.noSessions')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className={`w-full ${CASH_TABLE_CLASS}`}>
                    <thead className="border-b bg-slate-50/50">
                      <tr>
                        <th className={CASH_TABLE_HEAD_LEFT}>{t('labels.code')}</th>
                        <th className={CASH_TABLE_HEAD_LEFT}>{t('labels.location')}</th>
                        <th className={CASH_TABLE_HEAD_RIGHT}>{t('cashManagementPage.opening')}</th>
                        <th className={CASH_TABLE_HEAD_RIGHT}>{t('cashManagementPage.closing')}</th>
                        <th className={CASH_TABLE_HEAD_RIGHT}>{t('cashManagementPage.difference')}</th>
                        <th className={CASH_TABLE_HEAD_CENTER}>{t('labels.status')}</th>
                        <th className={CASH_TABLE_HEAD_CENTER}>{t('salesPage.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSessions.map((session) => {
                        const statusConfig = sessionStatusConfig[session.status] || sessionStatusConfig.open;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <tr key={session.id} className="border-b last:border-0 hover:bg-slate-50/50">
                            <td className="px-3 py-2">
                              <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">{session.session_code}</code>
                            </td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="text-[10px]">
                                <MapPin className="h-3 w-3 mr-1" />
                                {session.location_name}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div>
                                <p className="font-medium">{formatCurrency(session.opening_balance)}</p>
                                <p className="text-[10px] text-muted-foreground">{formatDateTime(session.opened_at)}</p>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {session.status === 'closed' ? (
                                <div>
                                  <p className="font-medium">{formatCurrency(session.closing_balance)}</p>
                                  <p className="text-[10px] text-muted-foreground">{formatDateTime(session.closed_at)}</p>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {session.difference !== null && session.difference !== undefined ? (
                                <span className={cn(
                                  "font-medium",
                                  session.difference > 0 ? "text-green-600" : 
                                  session.difference < 0 ? "text-red-600" : ""
                                )}>
                                  {session.difference > 0 ? '+' : ''}{formatCurrency(session.difference)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Badge variant="outline" className={cn('text-[10px]', statusConfig.color)}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Button size="sm" variant="ghost" className={COMPACT_ACTION_BUTTONS.icon} onClick={() => handleViewSession(session)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transfers Tab */}
        <TabsContent value="transfers" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200" data-testid="cash-transfer-summary-in">{t('cashManagementPage.transferSummary.in', { count: transferCounts.in })}</Badge>
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid="cash-transfer-summary-out">{t('cashManagementPage.transferSummary.out', { count: transferCounts.out })}</Badge>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid="cash-transfer-summary-transfer">{t('cashManagementPage.transferSummary.transfer', { count: transferCounts.transfer })}</Badge>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:w-[30rem]">
                  <Select value={transferDirectionFilter} onValueChange={setTransferDirectionFilter}>
                    <SelectTrigger data-testid="cash-transfer-direction-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('cashManagementPage.filters.allDirections')}</SelectItem>
                      <SelectItem value="in">{t('cashManagementPage.directionLabels.in')}</SelectItem>
                      <SelectItem value="out">{t('cashManagementPage.directionLabels.out')}</SelectItem>
                      <SelectItem value="transfer">{t('cashManagementPage.directionLabels.transfer')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={transferOperationFilter} onValueChange={setTransferOperationFilter}>
                    <SelectTrigger data-testid="cash-transfer-operation-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('cashManagementPage.filters.allOperationTypes')}</SelectItem>
                      {operationTypes.map((operationType) => (
                        <SelectItem key={operationType.id} value={operationType.id}>{operationType.code} · {operationType.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredTransfers.length === 0 ? (
                <div className="text-center py-12">
                  <ArrowRightLeft className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">{t('cashManagementPage.noTransfers')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className={`w-full ${CASH_TABLE_CLASS}`}>
                    <thead className="border-b bg-slate-50/50">
                      <tr>
                        <th className={CASH_TABLE_HEAD_LEFT}>{t('labels.code')}</th>
                        <th className={CASH_TABLE_HEAD_LEFT}>{t('cashManagementPage.type')}</th>
                        <th className={CASH_TABLE_HEAD_LEFT}>{t('cashManagementPage.sourceDestination')}</th>
                        <th className={CASH_TABLE_HEAD_RIGHT}>{t('labels.amount')}</th>
                        <th className={CASH_TABLE_HEAD_LEFT}>{t('cashManagementPage.reason')}</th>
                        <th className={CASH_TABLE_HEAD_LEFT}>{t('labels.date')}</th>
                        <th className={CASH_TABLE_HEAD_CENTER}>{t('salesPage.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransfers.map((transfer) => {
                        const dirConfig = directionConfig[transfer.operation_direction] || directionConfig.transfer;
                        
                        return (
                          <tr key={transfer.id} className="border-b last:border-0 hover:bg-slate-50/50">
                            <td className="px-3 py-2">
                              <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">{transfer.transfer_code}</code>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={cn("text-[10px]", dirConfig.bgColor, dirConfig.color)} data-testid={`cash-transfer-direction-badge-${transfer.id}`}>
                                  {dirConfig.label}
                                </Badge>
                                <Badge variant="outline" className={cn("text-[10px]", dirConfig.bgColor, dirConfig.color)}>
                                  {transfer.operation_type_code}
                                </Badge>
                                <span className="text-[10px] text-muted-foreground">{transfer.operation_type_name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2 text-[11px]">
                                <span>{transfer.source_account_name}</span>
                                {transfer.destination_account_name && (
                                  <>
                                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                    <span>{transfer.destination_account_name}</span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span className={cn("font-semibold", dirConfig.color)}>
                                {formatCurrency(transfer.amount)}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className="line-clamp-1 text-[11px]">{transfer.reason}</span>
                            </td>
                            <td className="px-3 py-2">
                              <span className="text-[10px] text-muted-foreground">{formatDateTime(transfer.created_at)}</span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Button size="sm" variant="ghost" className={COMPACT_ACTION_BUTTONS.icon} onClick={() => handleViewTransfer(transfer)} title="Ver Detalhes">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">{t('cashManagementPage.systemAlerts')}</CardTitle>
                  {unresolvedAlertsCount > 0 && (
                    <Badge variant="destructive">{t('cashManagementPage.pendingBadge', { count: unresolvedAlertsCount })}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={alertStatusFilter} onValueChange={setAlertStatusFilter}>
                    <SelectTrigger className="w-36" data-testid="alert-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">{t('cashManagementPage.alertFilters.open')}</SelectItem>
                      <SelectItem value="resolved">{t('cashManagementPage.alertFilters.resolved')}</SelectItem>
                      <SelectItem value="invalid_scope">{t('cashManagementPage.alertFilters.invalidScope')}</SelectItem>
                      <SelectItem value="stale_history">{t('cashManagementPage.alertFilters.staleHistory')}</SelectItem>
                      <SelectItem value="all">{t('cashManagementPage.alertFilters.all')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={alertTypeFilter} onValueChange={setAlertTypeFilter}>
                    <SelectTrigger className="w-44" data-testid="alert-type-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('cashManagementPage.filters.allAlertTypes')}</SelectItem>
                      <SelectItem value="missed_checkpoint">{t('cashManagementPage.alertTypes.missedCheckpoint')}</SelectItem>
                      <SelectItem value="no_opening">{t('cashManagementPage.alertTypes.noOpening')}</SelectItem>
                      <SelectItem value="no_closing">{t('cashManagementPage.alertTypes.noClosing')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={loadAlertsWithFilter} data-testid="cash-alert-refresh-button">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200" data-testid="cash-alert-summary-missed-checkpoint">{t('cashManagementPage.alertSummary.missedCheckpoint', { count: alertCounts.missed_checkpoint })}</Badge>
                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200" data-testid="cash-alert-summary-no-opening">{t('cashManagementPage.alertSummary.noOpening', { count: alertCounts.no_opening })}</Badge>
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200" data-testid="cash-alert-summary-no-closing">{t('cashManagementPage.alertSummary.noClosing', { count: alertCounts.no_closing })}</Badge>
                <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200" data-testid="cash-alert-summary-invalid-scope">{classificationSummary?.counts?.invalid_location_scope || 0} invalid_scope</Badge>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid="cash-alert-summary-stale-history">{classificationSummary?.counts?.stale_past_day || 0} stale_history</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {filteredAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {alertStatusFilter === 'open' ? t('cashManagementPage.noOpenAlerts') : 
                     alertStatusFilter === 'resolved' ? t('cashManagementPage.noResolvedAlerts') : 
                     t('cashManagementPage.noAlerts')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAlerts.map((alert) => (
                    <div 
                      key={alert.id} 
                      className={cn(
                        "flex items-start justify-between p-3 border rounded-xl",
                        alert.is_resolved 
                          ? "bg-slate-50 border-slate-200" 
                          : "bg-amber-50 border-amber-200"
                      )}
                      data-testid={`alert-${alert.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {alert.is_resolved ? (
                          <Check className="h-5 w-5 text-green-600 mt-0.5" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                        )}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <code className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">
                              {alert.alert_code || 'N/A'}
                            </code>
                            <Badge 
                              variant="outline" 
                              className={cn('text-xs', alertTypeConfig[alert.alert_type]?.className || 'bg-slate-100 text-slate-700 border-slate-200')}
                            >
                              {alertTypeConfig[alert.alert_type]?.label || alert.alert_type || '—'}
                            </Badge>
                            {alert.severity && (
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-xs',
                                  alert.severity === 'critical' && 'bg-rose-100 text-rose-700 border-rose-200',
                                  alert.severity === 'high' && 'bg-orange-100 text-orange-700 border-orange-200',
                                  alert.severity === 'warning' && 'bg-amber-100 text-amber-700 border-amber-200',
                                  alert.severity === 'medium' && 'bg-amber-100 text-amber-700 border-amber-200',
                                  alert.severity === 'low' && 'bg-slate-100 text-slate-700 border-slate-200'
                                )}
                              >
                                {t(`cashManagementPage.severityLabels.${alert.severity}`)}
                              </Badge>
                            )}
                            <Badge variant="outline" className={cn('text-xs', alertStatusConfig[alert.status || (alert.is_resolved ? 'resolved' : 'open')]?.className || ALERT_STATUS_STYLES.open)}>
                              {alertStatusConfig[alert.status || (alert.is_resolved ? 'resolved' : 'open')]?.label || alert.status || 'open'}
                            </Badge>
                            {alert.archival_reason_code && (
                              <Badge variant="outline" className="text-xs bg-slate-100 text-slate-700 border-slate-200">
                                {t(`cashManagementPage.archivalReasonLabels.${alert.archival_reason_code}`)}
                              </Badge>
                            )}
                          </div>
                          <p className="text-[11px] font-medium">{alert.message}</p>
                          <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {alert.location_name}
                            </span>
                            <span>•</span>
                            <span>{formatDateTime(alert.created_at)}</span>
                            {alert.scheduled_time && (
                              <>
                                <span>•</span>
                                <span className="text-amber-700">
                                  Horário: {new Date(alert.scheduled_time).toLocaleTimeString('pt-PT', {hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Maputo'})}
                                </span>
                              </>
                            )}
                          </div>
                          {alert.is_resolved && (
                            <div className={cn(
                              'mt-2 p-2 rounded text-sm border',
                              alert.status === 'resolved' ? 'bg-green-50 border-green-100' : 'bg-slate-50 border-slate-200'
                            )}>
                              <p className={cn(alert.status === 'resolved' ? 'text-green-800' : 'text-slate-800')}>
                                <strong>{alert.status === 'resolved' ? 'Resolvido por:' : `${t('cashManagementPage.archivedAt')}:`}</strong> {alert.resolved_by_name} em {formatDateTime(alert.resolved_at)}
                              </p>
                              {alert.resolution_notes && (
                                <p className={cn('mt-1', alert.status === 'resolved' ? 'text-green-700' : 'text-slate-700')}>
                                  <strong>Comentário:</strong> {alert.resolution_notes}
                                </p>
                              )}
                              {alert.sanitation_batch_id && (
                                <p className="text-slate-600 mt-1">
                                  <strong>{t('cashManagementPage.archivedBatch')}:</strong> {alert.sanitation_batch_id}
                                </p>
                              )}
                              {alert.archival_reason_code && (
                                <p className="text-slate-600 mt-1">
                                  <strong>{t('cashManagementPage.archivalReason')}:</strong> {t(`cashManagementPage.archivalReasonLabels.${alert.archival_reason_code}`)}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {!alert.is_resolved && alert.alert_type === 'no_opening' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className={COMPACT_ACTION_BUTTONS.tab}
                            onClick={() => handleOpenSessionFromAlert(alert)}
                            data-testid={`cash-alert-open-session-btn-${alert.id}`}
                          >
                            <Play className="mr-1 h-3.5 w-3.5" />
                            {t('cashManagementPage.openSessionForAlert')}
                          </Button>
                        )}
                        {!alert.is_resolved && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className={COMPACT_ACTION_BUTTONS.tab}
                            onClick={() => handleOpenResolveDialog(alert)}
                            data-testid={`resolve-alert-btn-${alert.id}`}
                          >
                            <Check className="mr-1 h-3.5 w-3.5" />
                            {t('cashManagementPage.resolve')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </ModuleStateBoundary>

      {/* Open Session Dialog */}
      <Dialog open={openSessionDialogOpen} onOpenChange={setOpenSessionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-green-600" />
              {t('cashManagementPage.openSessionTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Localização *</Label>
              <Select 
                value={openSessionData.location_id} 
                onValueChange={(v) => setOpenSessionData({...openSessionData, location_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar local" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Saldo Inicial (MZN) *</Label>
              <Input
                type="number"
                step="0.01"
                value={openSessionData.opening_balance}
                onChange={(e) => setOpenSessionData({...openSessionData, opening_balance: e.target.value})}
                placeholder="0.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={openSessionData.notes}
                onChange={(e) => setOpenSessionData({...openSessionData, notes: e.target.value})}
                placeholder="Observações opcionais..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Data/Hora de Abertura
                <span className="text-xs text-muted-foreground">(opcional)</span>
              </Label>
              <Input
                type="datetime-local"
                value={openSessionData.opened_at || ''}
                onChange={(e) => setOpenSessionData({...openSessionData, opened_at: e.target.value})}
                max={new Date().toISOString().slice(0, 16)}
                className="border-amber-200 focus:border-amber-400"
              />
              <p className="text-xs text-muted-foreground">
                Deixe vazio para usar a data/hora actual
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setOpenSessionDialogOpen(false)}>{t('actions.cancel')}</Button>
              <Button onClick={handleOpenSession}>{t('cashManagementPage.openSessionAction')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Session Dialog */}
      <AlertDialog open={closeSessionDialogOpen} onOpenChange={setCloseSessionDialogOpen}>
        <AlertDialogContent aria-describedby="cash-close-session-description">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Square className="h-5 w-5 text-red-600" />
              {t('cashManagementPage.closeSessionTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription id="cash-close-session-description">
              {t('cashManagementPage.closeSessionDescription')}
            </AlertDialogDescription>
            {activeSession && (
                <div className="mt-4 bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Saldo Inicial:</span>
                    <span className="font-medium">{formatCurrency(activeSession.opening_balance)}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>+ Entradas:</span>
                    <span>{formatCurrency(activeSession.total_transfers_in || 0)}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>- Saídas:</span>
                    <span>{formatCurrency(activeSession.total_transfers_out || 0)}</span>
                  </div>
                  <div className="flex justify-between text-amber-600">
                    <span>- Despesas:</span>
                    <span>{formatCurrency(activeSession.total_expenses || 0)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold">
                    <span>Saldo Esperado:</span>
                    <span className="text-green-600">{formatCurrency(expectedBalance)}</span>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Saldo de Fecho (MZN) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={closeSessionData.closing_balance}
                  onChange={(e) => setCloseSessionData({...closeSessionData, closing_balance: e.target.value})}
                  placeholder="Conte o dinheiro e informe o valor"
                />
              </div>
              
              {closeSessionData.closing_balance && Math.abs(parseFloat(closeSessionData.closing_balance) - expectedBalance) > 1 && (
                <div className="space-y-2">
                  <Label className="text-amber-700">Justificação da Diferença *</Label>
                  <Textarea
                    value={closeSessionData.difference_justification}
                    onChange={(e) => setCloseSessionData({...closeSessionData, difference_justification: e.target.value})}
                    placeholder="Explique a diferença encontrada..."
                    rows={2}
                  />
                </div>
              )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCloseSession} className="bg-red-600 hover:bg-red-700">
              {t('cashManagementPage.closeSessionAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-lg" aria-describedby="cash-transfer-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
              {t('cashManagementPage.transferDialogTitle')}
            </DialogTitle>
            <p id="cash-transfer-dialog-description" className="text-sm text-muted-foreground">{t('cashManagementPage.transferDialogDescription')}</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Operação *</Label>
              <Select 
                value={transferData.operation_type_id} 
                onValueChange={(v) => setTransferData({...transferData, operation_type_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar operação" />
                </SelectTrigger>
                <SelectContent>
                  {operationTypes.map(op => (
                    <SelectItem key={op.id} value={op.id}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{op.code}</Badge>
                        {op.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Conta de Origem *</Label>
                <Select 
                  value={transferData.source_account_id} 
                  onValueChange={(v) => setTransferData({...transferData, source_account_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="De onde?" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedOperationType?.direction === 'transfer' && (
                <div className="space-y-2">
                  <Label>Conta de Destino *</Label>
                  <Select 
                    value={transferData.destination_account_id} 
                    onValueChange={(v) => setTransferData({...transferData, destination_account_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Para onde?" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentAccounts.filter(a => a.id !== transferData.source_account_id).map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {(selectedOperationType?.code?.startsWith('TRF11') || selectedOperationType?.code?.startsWith('TRF12')) && (
              <div className="space-y-2">
                <Label>Beneficiário</Label>
                <Input
                  value={transferData.beneficiary_name}
                  onChange={(e) => setTransferData({...transferData, beneficiary_name: e.target.value})}
                  placeholder="Nome do funcionário ou terceiro"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valor (MZN) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={transferData.amount}
                  onChange={(e) => setTransferData({...transferData, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Localização</Label>
                <Select 
                  value={transferData.location_id} 
                  onValueChange={(v) => setTransferData({...transferData, location_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Local" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Input
                value={transferData.reason}
                onChange={(e) => setTransferData({...transferData, reason: e.target.value})}
                placeholder="Justificação obrigatória"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Referência Externa</Label>
              <Input
                value={transferData.reference}
                onChange={(e) => setTransferData({...transferData, reference: e.target.value})}
                placeholder="Nº de cheque, transferência, etc."
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Data/Hora do Registo
                <span className="text-xs text-muted-foreground">(opcional - deixe vazio para usar data actual)</span>
              </Label>
              <Input
                type="datetime-local"
                value={transferData.registered_at}
                onChange={(e) => setTransferData({...transferData, registered_at: e.target.value})}
                max={new Date().toISOString().slice(0, 16)}
                className="border-amber-200 focus:border-amber-400"
              />
              <p className="text-xs text-muted-foreground">
                Use este campo se a operação foi realizada numa data/hora diferente
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>{t('actions.cancel')}</Button>
              <Button onClick={handleCreateTransfer}>{t('cashManagementPage.registerTransfer')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkpoint Dialog */}
      <Dialog open={checkpointDialogOpen} onOpenChange={setCheckpointDialogOpen}>
        <DialogContent aria-describedby="cash-checkpoint-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-indigo-600" />
              {t('cashManagementPage.checkpointDialogTitle')}
            </DialogTitle>
            <p id="cash-checkpoint-dialog-description" className="text-sm text-muted-foreground">{t('cashManagementPage.checkpointDialogDescription')}</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="text-blue-800">
                {t('cashManagementPage.checkpointInfo')}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Localização *</Label>
              <Select 
                value={checkpointData.location_id} 
                onValueChange={(v) => setCheckpointData({...checkpointData, location_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar local" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Saldo Actual (MZN) *</Label>
              <Input
                type="number"
                step="0.01"
                value={checkpointData.balance}
                onChange={(e) => setCheckpointData({...checkpointData, balance: e.target.value})}
                placeholder="Conte e informe o valor em caixa"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={checkpointData.notes}
                onChange={(e) => setCheckpointData({...checkpointData, notes: e.target.value})}
                placeholder="Observações opcionais..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Data/Hora do Checkpoint
                <span className="text-xs text-muted-foreground">(opcional)</span>
              </Label>
              <Input
                type="datetime-local"
                value={checkpointData.checkpoint_time || ''}
                onChange={(e) => setCheckpointData({...checkpointData, checkpoint_time: e.target.value})}
                max={new Date().toISOString().slice(0, 16)}
                className="border-amber-200 focus:border-amber-400"
              />
              <p className="text-xs text-muted-foreground">
                Deixe vazio para usar a data/hora actual
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCheckpointDialogOpen(false)}>{t('actions.cancel')}</Button>
              <Button onClick={handleRegisterCheckpoint}>{t('cashManagementPage.registerCheckpoint')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Details Dialog */}
      <Dialog open={sessionDetailsOpen} onOpenChange={setSessionDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('cashManagementPage.sessionDetailsTitle')}</DialogTitle>
          </DialogHeader>
          {selectedSession && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <code className="text-lg font-mono bg-slate-100 px-2 py-1 rounded">
                    {selectedSession.session_code}
                  </code>
                  <Badge 
                    variant="outline" 
                    className={cn("ml-3", sessionStatusConfig[selectedSession.status]?.color)}
                  >
                    {sessionStatusConfig[selectedSession.status]?.label}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('labels.location')}</p>
                  <p className="font-medium">{selectedSession.location_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('cashManagementPage.openedBy')}</p>
                  <p>{selectedSession.opened_by_name} às {formatDateTime(selectedSession.opened_at)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4 bg-slate-50 rounded-lg p-4">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">{t('cashManagementPage.openingBalance')}</p>
                  <p className="font-bold">{formatCurrency(selectedSession.opening_balance)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">{t('cashManagementPage.entries')}</p>
                  <p className="font-bold text-green-600">+{formatCurrency(selectedSession.total_transfers_in || 0)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">{t('cashManagementPage.exits')}</p>
                  <p className="font-bold text-red-600">-{formatCurrency(selectedSession.total_transfers_out || 0)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">{t('cashManagementPage.expenses')}</p>
                  <p className="font-bold text-amber-600">-{formatCurrency(selectedSession.total_expenses || 0)}</p>
                </div>
              </div>
              
              {selectedSession.status === 'closed' && (
                <div className="grid grid-cols-3 gap-4 border-t pt-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Saldo Esperado</p>
                    <p className="font-medium">{formatCurrency(selectedSession.expected_balance)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Saldo Real</p>
                    <p className="font-medium">{formatCurrency(selectedSession.closing_balance)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Diferença</p>
                    <p className={cn(
                      "font-bold",
                      selectedSession.difference > 0 ? "text-green-600" : 
                      selectedSession.difference < 0 ? "text-red-600" : ""
                    )}>
                      {selectedSession.difference > 0 ? '+' : ''}{formatCurrency(selectedSession.difference || 0)}
                    </p>
                  </div>
                </div>
              )}
              
              {selectedSession.difference_justification && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>{t('cashManagementPage.justification')}:</strong> {selectedSession.difference_justification}
                  </p>
                </div>
              )}
              
              {selectedSession.closed_by_name && (
                <div className="text-sm text-muted-foreground border-t pt-4">
                  {t('cashManagementPage.closedByAt', { name: selectedSession.closed_by_name, date: formatDateTime(selectedSession.closed_at) })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Details Dialog */}
      <Dialog open={transferDetailsOpen} onOpenChange={setTransferDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="cash-transfer-details-description">
          <DialogHeader>
            <DialogTitle>{t('cashManagementPage.transferDetailsTitle')}</DialogTitle>
            <p id="cash-transfer-details-description" className="text-sm text-muted-foreground">{t('cashManagementPage.transferDetailsDescription')}</p>
          </DialogHeader>
          {selectedTransfer && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <code className="text-lg font-mono bg-slate-100 px-2 py-1 rounded">
                    {selectedTransfer.transfer_code}
                  </code>
                  <Badge 
                    variant="outline" 
                    className={cn("ml-3", DIRECTION_CONFIG[selectedTransfer.operation_direction]?.bgColor, DIRECTION_CONFIG[selectedTransfer.operation_direction]?.color)}
                  >
                    {selectedTransfer.operation_type_code}
                  </Badge>
                </div>
                <span className={cn("text-2xl font-bold", DIRECTION_CONFIG[selectedTransfer.operation_direction]?.color)}>
                  {formatCurrency(selectedTransfer.amount)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('cashManagementPage.operationType')}</p>
                  <p className="font-medium">{selectedTransfer.operation_type_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('labels.location')}</p>
                  <p>{selectedTransfer.location_name}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('cashManagementPage.sourceAccount')}</p>
                  <p>{selectedTransfer.source_account_name}</p>
                </div>
                {selectedTransfer.destination_account_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('cashManagementPage.destinationAccount')}</p>
                    <p>{selectedTransfer.destination_account_name}</p>
                  </div>
                )}
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">{t('cashManagementPage.reason')}</p>
                <p className="font-medium">{selectedTransfer.reason}</p>
              </div>
              
              {selectedTransfer.beneficiary_name && (
                <div>
                  <p className="text-sm text-muted-foreground">{t('cashManagementPage.beneficiary')}</p>
                  <p>{selectedTransfer.beneficiary_name}</p>
                </div>
              )}
              
              {selectedTransfer.reference && (
                <div>
                  <p className="text-sm text-muted-foreground">{t('cashManagementPage.externalReference')}</p>
                  <p className="font-mono">{selectedTransfer.reference}</p>
                </div>
              )}
              
              {/* Attachments */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium mb-3 block">{t('cashManagementPage.attachments')}</Label>
                <AttachmentUploader
                  entityType="cash_transfer"
                  entityId={selectedTransfer.transfer_code}
                  attachments={selectedTransferAttachments}
                  onAttachmentsChange={setSelectedTransferAttachments}
                  compact
                />
              </div>
              
              {/* Audit info */}
              <div className="text-sm text-muted-foreground border-t pt-4">
                {t('cashManagementPage.registeredByAt', { name: selectedTransfer.created_by_name, date: formatDateTime(selectedTransfer.created_at) })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resolve Alert Dialog */}
      <AlertDialog open={resolveAlertDialogOpen} onOpenChange={setResolveAlertDialogOpen}>
        <AlertDialogContent aria-describedby="cash-resolve-alert-description">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {t('cashManagementPage.resolveAlertTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription id="cash-resolve-alert-description">
              {t('cashManagementPage.resolveAlertDescription')}
            </AlertDialogDescription>
            {selectedAlert && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <code className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">
                      {selectedAlert.alert_code || 'N/A'}
                    </code>
                    <Badge variant="outline" className="text-xs">
                      {selectedAlert.alert_type === 'missed_checkpoint' && t('cashManagementPage.alertTypes.missedCheckpoint')}
                      {selectedAlert.alert_type === 'no_opening' && t('cashManagementPage.alertTypes.noOpening')}
                      {selectedAlert.alert_type === 'no_closing' && t('cashManagementPage.alertTypes.noClosing')}
                    </Badge>
                  </div>
                  <p className="font-medium text-amber-900">{selectedAlert.message}</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {selectedAlert.location_name} • {formatDateTime(selectedAlert.created_at)}
                  </p>
                </div>
              )}
              
              <div className="space-y-2 text-left">
                <Label className="text-slate-900">
                  {t('cashManagementPage.resolutionComment')} <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder={t('cashManagementPage.resolutionPlaceholder')}
                  rows={3}
                  className="w-full"
                  data-testid="resolution-notes-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t('cashManagementPage.resolutionHint')}
                </p>
              </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setSelectedAlert(null);
              setResolutionNotes('');
            }}>
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResolveAlert}
              disabled={!resolutionNotes.trim()}
              data-testid="confirm-resolve-btn"
            >
              <Check className="h-4 w-4 mr-1" />
              {t('cashManagementPage.confirmResolution')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
